/**
 * Agreement Signatures — IN-1.c
 * ─────────────────────────────
 * Append-only writer for `Contractor.agreementSignatures`. Pure: returns
 * a new Contractor; never mutates the input.
 *
 * IN-1.c is **observation-only**. No UI / dispatch / payout / vetting
 * consumer may import this writer until IN-1.d explicitly approves a
 * surface. Tests + this module are the only legal importers.
 *
 * Hard rules:
 *   • append-only — never overwrites a prior signature
 *   • `variant` must match `getRequiredCompliance(contractor).agreementVariant`
 *   • `actor` (signedBy) is mandatory
 *   • `version` is mandatory
 *
 * Anchor: RECONCILIATION.md §14 (IN-1.c)
 */

import type { Contractor } from "@/data/types";
import { getRequiredCompliance, type AgreementVariant } from "./complianceRequirements";

export interface RecordAgreementSignatureInput {
  variant: AgreementVariant;
  version: string;
  ipAddress?: string;
  /** Optional explicit signedAt; defaults to now. */
  signedAt?: string;
}

export type AgreementSignature = NonNullable<Contractor["agreementSignatures"]>[number];

let __sigCounter = 0;
function nextId(): string {
  __sigCounter += 1;
  return `asig-${Date.now().toString(36)}-${__sigCounter.toString(36)}`;
}

function requireNonEmpty(value: string | undefined, field: string): string {
  const v = (value ?? "").trim();
  if (!v) throw new Error(`agreementSignatures: ${field} is required.`);
  return v;
}

/**
 * Append an agreement signature to a contractor. Pure — returns a new
 * Contractor reference. Throws if the variant does not match the
 * requirement matrix for this entity type.
 */
export function recordAgreementSignature(
  contractor: Contractor,
  input: RecordAgreementSignatureInput,
  actor: string,
  parent?: Pick<Contractor, "id" | "entityType">,
): { contractor: Contractor; signature: AgreementSignature } {
  if (!contractor) {
    throw new Error("agreementSignatures: contractor is required.");
  }
  const signedBy = requireNonEmpty(actor, "actor");
  const version = requireNonEmpty(input?.version, "version");
  const variant = input?.variant;
  if (!variant) {
    throw new Error("agreementSignatures: variant is required.");
  }

  const reqs = getRequiredCompliance(contractor, parent);
  if (variant !== reqs.agreementVariant) {
    throw new Error(
      `agreementSignatures: variant "${variant}" does not match required ` +
        `variant "${reqs.agreementVariant}" for entityType ` +
        `"${contractor.entityType ?? "independent"}".`,
    );
  }

  const signature: AgreementSignature = {
    id: nextId(),
    variant,
    version,
    signedAt: input.signedAt ?? new Date().toISOString(),
    signedBy,
    ipAddress: input.ipAddress,
  };
  const next: Contractor = {
    ...contractor,
    agreementSignatures: [...(contractor.agreementSignatures ?? []), signature],
  };
  return { contractor: next, signature };
}

/**
 * Return the latest signature whose variant matches the current
 * requirement, or undefined if none exists.
 */
export function getLatestValidAgreementSignature(
  contractor: Pick<Contractor, "entityType" | "parentEntityId" | "agreementSignatures">,
  parent?: Pick<Contractor, "id" | "entityType">,
): AgreementSignature | undefined {
  const reqs = getRequiredCompliance(contractor, parent);
  const matches = (contractor.agreementSignatures ?? []).filter(
    (s) => s.variant === reqs.agreementVariant,
  );
  if (!matches.length) return undefined;
  return [...matches].sort(
    (a, b) => Date.parse(b.signedAt) - Date.parse(a.signedAt),
  )[0];
}
